/**
 * One identity provider's email domains and sign-in routing (VSNP-1629, doc
 * 28d §10.1), on Administration → Identity Providers.
 *
 * - Domains: add one (it starts pending and shows the DNS TXT record to
 *   publish), Verify (the API resolves the record now), Attest (SuperAdmin
 *   only, with a reason: for lab and private domains with no public DNS),
 *   Remove. A domain routes nobody until it is verified or attested.
 * - Routing: "Everyone in the tenant" (`all`) or "Only these domains"
 *   (`domains`), which needs a verified domain. Saving it — and removing or
 *   attesting a domain — opens the preview of who changes sign-in method
 *   (RoutingPreviewDialog); Cancel sends nothing.
 *
 * Every action here is its own API call, applied at once; there is no form to
 * save, which is why this is a card on the page rather than a section of the
 * provider's credentials dialog (whose Cancel must not undo a domain action).
 */
import { useState } from 'react';
import { CheckCircle2, Copy, Globe, ShieldCheck, Trash2, AlertTriangle, RefreshCw } from 'lucide-react';
import { useToast } from '../common/ToastContainer';
import RoutingPreviewDialog from './RoutingPreviewDialog';
import accessService, {
  confirmRequiredPreview,
  type IdentityProvider,
  type IdentityProviderDomain,
  type IdentityProviderRouting,
  type RoutingPreview,
} from '../../services/access.service';
import { apiErrorMessage } from '../../utils/apiError';
import { formatDate } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';
import {
  PROVIDER_TYPE_LABEL,
  domainBadge,
  domainLabel,
  providerDomains,
  providerRouting,
  routingWouldMix,
  NO_VERIFIED_DOMAIN_NOTICE,
  needsVerifiedDomainNotice,
  verifiedDomains,
} from '../../utils/signInRouting';

type DialogState =
  | { kind: 'routing'; to: IdentityProviderRouting }
  | { kind: 'remove'; domain: IdentityProviderDomain }
  | { kind: 'attest'; domain: IdentityProviderDomain };

interface ProviderRoutingCardProps {
  provider: IdentityProvider;
  /** Every provider of the tenant (the routing of the others decides whether a switch would mix). */
  providers: IdentityProvider[];
  isSuperAdmin: boolean;
  /** Re-read the providers (and the activity) after a write. */
  onChanged: () => Promise<void> | void;
}

export const DomainStatusBadge = ({ domain }: { domain: Pick<IdentityProviderDomain, 'status' | 'verificationMethod' | 'lastCheckError'> }) => {
  const b = domainBadge(domain);
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${b.className}`}
      data-testid="idp-domain-status"
      data-status={b.kind}
    >
      {b.label}
    </span>
  );
};

/** An enabled provider for everyone with no verified domain (see NO_VERIFIED_DOMAIN_NOTICE). */
export const NoVerifiedDomainBadge = () => (
  <span
    className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-warning-100 text-warning-700"
    title={NO_VERIFIED_DOMAIN_NOTICE}
    data-testid="idp-no-verified-domain-badge"
  >
    <AlertTriangle className="w-3 h-3" />
    No verified domain
  </span>
);

const ProviderRoutingCard = ({ provider: p, providers, isSuperAdmin, onChanged }: ProviderRoutingCardProps) => {
  const toast = useToast();
  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const current = providerRouting(p);
  const domains = providerDomains(p);
  const hasVerified = verifiedDomains(p).length > 0;
  const needsDomainNotice = needsVerifiedDomainNotice(p);

  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  // The radio's unsaved choice; null = the saved routing (so a re-read of the
  // provider is shown as soon as it arrives).
  const [draftChoice, setDraftChoice] = useState<IdentityProviderRouting | null>(null);
  const draft = draftChoice ?? current;
  const setDraft = (v: IdentityProviderRouting) => setDraftChoice(v === current ? null : v);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [preview, setPreview] = useState<RoutingPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const wouldMix = draft !== current && routingWouldMix(providers, p.id, { routing: draft });
  const others = providers.filter((o) => o.id !== p.id && o.enabled);

  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied', what);
    } catch {
      toast.error('Copy failed', 'Select and copy the value manually');
    }
  };

  const openDialog = async (d: DialogState) => {
    setDialog(d);
    setReason('');
    setPreview(null);
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      setPreview(
        await accessService.getRoutingPreview(
          d.kind === 'routing'
            ? { providerId: p.id, routing: d.to }
            : d.kind === 'remove'
              ? { providerId: p.id, removeDomain: d.domain.domain }
              : { providerId: p.id, addDomain: d.domain.domain },
        ),
      );
    } catch (err) {
      setPreviewError(apiErrorMessage(err, 'preview unavailable'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const closeDialog = () => {
    // Cancel changes nothing: the radio goes back to the saved routing.
    if (dialog?.kind === 'routing') setDraftChoice(null);
    setDialog(null);
    setPreview(null);
    setPreviewError(null);
    setReason('');
  };

  const addDomain = async () => {
    const value = newDomain.trim();
    if (!value) return;
    try {
      setAdding(true);
      const d = await accessService.addIdentityProviderDomain(p.id, value);
      toast.success('Domain added', `${domainLabel(d)} is pending: publish the TXT record, then Verify`);
      setNewDomain('');
      await onChanged();
    } catch (err) {
      toast.error('Could not add the domain', apiErrorMessage(err, 'Request failed'));
    } finally {
      setAdding(false);
    }
  };

  const verify = async (d: IdentityProviderDomain) => {
    try {
      setBusy(d.id);
      const r = await accessService.verifyIdentityProviderDomain(p.id, d.id);
      if (r.check.ok) toast.success(d.status === 'verified' ? 'DNS record still in place' : 'Domain verified', domainLabel(d));
      else toast.error('Domain not verified', r.check.error);
      await onChanged();
    } catch (err) {
      toast.error('Verify failed', apiErrorMessage(err, 'Request failed'));
    } finally {
      setBusy(null);
    }
  };

  const confirm = async () => {
    if (!dialog) return;
    // `confirm` only when the admin saw (and acknowledged) users moving; a
    // stale preview is caught by the API's own 409, which re-opens the counts.
    const consent = preview?.changed === true;
    try {
      setSaving(true);
      if (dialog.kind === 'routing') {
        await accessService.updateIdentityProvider(p.id, { routing: dialog.to, ...(consent ? { confirm: true } : {}) });
        toast.success('Routing saved', dialog.to === 'domains' ? `${p.displayName}: only its verified domains` : `${p.displayName}: everyone in the tenant`);
      } else if (dialog.kind === 'remove') {
        await accessService.removeIdentityProviderDomain(p.id, dialog.domain.id, {
          ...(consent ? { confirm: true } : {}),
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        });
        toast.success('Domain removed', domainLabel(dialog.domain));
      } else {
        await accessService.attestIdentityProviderDomain(p.id, dialog.domain.id, reason.trim());
        toast.success('Domain ownership attested', domainLabel(dialog.domain));
      }
      setDialog(null);
      setPreview(null);
      setDraftChoice(null);
      await onChanged();
    } catch (err) {
      const fresh = confirmRequiredPreview(err);
      if (fresh) {
        // Users moved since the preview was read: show the API's counts again.
        setPreview(fresh);
        setPreviewError(null);
        toast.error('Confirm again', 'The number of users affected changed; review the preview.');
      } else {
        toast.error('Change refused', apiErrorMessage(err, 'Request failed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const dialogTitle =
    dialog?.kind === 'routing'
      ? `Change sign-in routing — ${p.displayName}`
      : dialog?.kind === 'remove'
        ? `Remove ${domainLabel(dialog.domain)}`
        : dialog
          ? `Attest ownership of ${domainLabel(dialog.domain)}`
          : '';

  const dialogDescription =
    dialog?.kind === 'routing' ? (
      dialog.to === 'domains' ? (
        <span>
          Send only users of <strong>{verifiedDomains(p).map((d) => `@${domainLabel(d)}`).join(', ')}</strong> to{' '}
          {p.displayName}. Everyone else in the tenant signs in with their SecureLink password.
        </span>
      ) : (
        <span>Send everyone in this tenant to {p.displayName}, whatever their email domain.</span>
      )
    ) : dialog?.kind === 'remove' ? (
      <span>
        <strong>{domainLabel(dialog.domain)}</strong> stops routing to {p.displayName}
        {dialog.domain.status === 'verified'
          ? current === 'domains'
            ? '; its users move to their SecureLink password'
            : ': the new apps no longer find this provider from that email domain before sign-in, and ask those users for a password'
          : ''}
        . A removed domain can be claimed again (and must be verified again).
      </span>
    ) : dialog ? (
      <span>
        Mark <strong>{domainLabel(dialog.domain)}</strong> as owned by this tenant without a DNS record (for lab and private
        domains). This is recorded with your name and the reason.
        {current === 'domains'
          ? ' Its users then sign in through this provider.'
          : ' The new apps then find this provider for users of that domain before sign-in.'}
      </span>
    ) : null;

  return (
    <div className="card space-y-4" data-testid="idp-routing-card" data-provider-id={p.id} data-routing={current}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Globe className="w-4 h-4 text-primary-600" />
            Sign-in routing — {p.displayName}
          </h2>
          <p className="text-xs text-gray-500">
            {PROVIDER_TYPE_LABEL[p.providerType]} · <span className="font-mono">{p.alias}</span>
            {!p.enabled && <span className="ml-1 text-warning-700">· disabled (routes nobody)</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
        {needsDomainNotice && <NoVerifiedDomainBadge />}
        <span
          className={`text-xs px-2 py-0.5 rounded-full ${current === 'domains' ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-700'}`}
          data-testid="idp-routing-current"
        >
          {current === 'domains' ? 'Only these domains' : 'Everyone in the tenant'}
        </span>
        </div>
      </div>

      {/* An enabled provider for everyone, with no verified domain: the new
          email-first apps cannot find it before sign-in (they learn the
          tenant only after it), so its users get a password prompt. */}
      {needsDomainNotice && (
        <p className="flex items-start gap-1.5 rounded border border-warning-200 bg-warning-50 px-3 py-2 text-xs text-warning-800" data-testid="idp-no-verified-domain-notice">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{NO_VERIFIED_DOMAIN_NOTICE}</span>
        </p>
      )}

      <p className="text-xs text-gray-600">
        The new SecureLink apps ask for the email first and find this provider ({PROVIDER_TYPE_LABEL[p.providerType]}) by its verified
        domains before sign-in. With "Everyone in the tenant" the provider still signs in every user of the tenant; with "Only these
        domains" everyone else uses their SecureLink password.
      </p>

      {/* Domains */}
      <div className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Domains</h3>
        {domains.length === 0 ? (
          <p className="text-xs text-gray-400" data-testid="idp-domains-empty">No domain listed yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100 rounded border border-gray-200">
            {domains.map((d) => (
              <li key={d.id} className="px-3 py-2 space-y-2" data-testid="idp-domain-row" data-domain={d.domain}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-medium text-gray-900 break-all">{domainLabel(d)}</span>
                    {d.domainUnicode && d.domainUnicode !== d.domain && (
                      <span className="font-mono text-xs text-gray-500 break-all" title="The A-label (punycode) the sign-in compares">
                        {d.domain}
                      </span>
                    )}
                    <DomainStatusBadge domain={d} />
                  </div>
                  <div className="flex items-center gap-1">
                    {(d.status !== 'verified' || d.verificationMethod === 'dns_txt') && (
                      <button
                        type="button"
                        className="inline-flex items-center px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                        onClick={() => void verify(d)}
                        disabled={busy === d.id}
                        title={d.status === 'verified' ? 'Check the DNS TXT record again' : 'Look up the DNS TXT record now'}
                        aria-label={`${d.status === 'verified' ? 'Re-check' : 'Verify'} ${domainLabel(d)}`}
                      >
                        <RefreshCw className={`w-3.5 h-3.5 mr-1 inline ${busy === d.id ? 'animate-spin' : ''}`} />
                        {d.status === 'verified' ? 'Re-check' : 'Verify'}
                      </button>
                    )}
                    {isSuperAdmin && d.status !== 'verified' && (
                      <button
                        type="button"
                        className="inline-flex items-center px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                        onClick={() => void openDialog({ kind: 'attest', domain: d })}
                        title="SuperAdmin: verify by attestation, with a reason"
                        aria-label={`Attest ${domainLabel(d)}`}
                      >
                        <ShieldCheck className="w-3.5 h-3.5 mr-1 inline" />
                        Attest…
                      </button>
                    )}
                    <button
                      type="button"
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-danger-600"
                      title="Remove domain"
                      aria-label={`Remove ${domainLabel(d)}`}
                      onClick={() => void openDialog({ kind: 'remove', domain: d })}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="text-xs text-gray-500 space-y-0.5">
                  {d.status === 'verified' && (
                    <div data-testid="idp-domain-verified">
                      {d.verificationMethod === 'operator' ? 'Attested' : 'Verified by DNS'}
                      {d.verifiedAt ? ` ${formatDate(d.verifiedAt, dateSettings)}` : ''}
                      {d.verifiedBy ? ` by ${d.verifiedBy}` : ''}
                    </div>
                  )}
                  {/* The DNS check matters for a pending or DNS-verified domain; an
                      attested one does not depend on it. */}
                  {d.lastCheckAt && d.verificationMethod !== 'operator' && (
                    <div>
                      Last DNS check {formatDate(d.lastCheckAt, dateSettings)}
                      {d.lastCheckError ? (
                        <span className="text-danger-700" data-testid="idp-domain-check-error">: {d.lastCheckError}</span>
                      ) : (
                        <span className="text-success-700"> — record found</span>
                      )}
                      {d.status === 'verified' && d.verificationMethod === 'dns_txt' && d.lastCheckError && (
                        <span className="block text-gray-500">It stays verified and keeps routing; remove it to stop.</span>
                      )}
                    </div>
                  )}
                </div>
                {d.status !== 'verified' && d.txtRecord && (
                  <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs space-y-1" data-testid="idp-domain-txt">
                    <p className="text-gray-600">Publish this DNS record, then press Verify:</p>
                    <div className="grid grid-cols-[4rem_1fr_auto] items-center gap-x-2 gap-y-1">
                      <span className="text-gray-500">Name</span>
                      <code className="break-all text-gray-800" data-testid="idp-domain-txt-name">{d.txtRecord.name}</code>
                      <button type="button" className="p-1 rounded hover:bg-gray-200 text-gray-500" title="Copy record name" onClick={() => void copy(d.txtRecord!.name, 'TXT record name')}>
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-gray-500">Type</span>
                      <code className="text-gray-800">TXT</code>
                      <span />
                      <span className="text-gray-500">Value</span>
                      <code className="break-all text-gray-800" data-testid="idp-domain-txt-value">{d.txtRecord.value}</code>
                      <button type="button" className="p-1 rounded hover:bg-gray-200 text-gray-500" title="Copy record value" onClick={() => void copy(d.txtRecord!.value, 'TXT record value')}>
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void addDomain();
          }}
        >
          <div className="flex-1">
            <label htmlFor={`idp-domain-add-${p.id}`} className="block text-xs font-medium text-gray-700 mb-1">
              Add a domain
            </label>
            <input
              id={`idp-domain-add-${p.id}`}
              className="input-base w-full font-mono"
              placeholder="customer.example"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              disabled={adding}
            />
          </div>
          <button type="submit" className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed" disabled={adding || !newDomain.trim()}>
            {adding ? 'Adding…' : 'Add domain'}
          </button>
        </form>
      </div>

      {/* Routing */}
      <fieldset className="space-y-2 border-t border-gray-200 pt-3">
        <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">Routing</legend>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name={`idp-routing-${p.id}`}
            className="mt-1"
            checked={draft === 'all'}
            onChange={() => setDraft('all')}
            aria-label="Everyone in the tenant"
            disabled={saving}
          />
          <span>
            <span className="font-medium text-gray-900">Everyone in the tenant</span>
            <span className="block text-xs text-gray-500">
              Send everyone in this tenant to this provider. The domains above are used only to find the provider before sign-in.
            </span>
          </span>
        </label>
        <label className={`flex items-start gap-2 text-sm ${!hasVerified && current !== 'domains' ? 'opacity-60' : ''}`}>
          <input
            type="radio"
            name={`idp-routing-${p.id}`}
            className="mt-1"
            checked={draft === 'domains'}
            onChange={() => setDraft('domains')}
            aria-label="Only these domains"
            disabled={saving || (!hasVerified && current !== 'domains')}
          />
          <span>
            <span className="font-medium text-gray-900">Only these domains</span>
            <span className="block text-xs text-gray-500">
              Send only users of the verified domains above to this provider; everyone else in the tenant signs in with their SecureLink
              password.
            </span>
            {!hasVerified && current !== 'domains' && (
              <span className="block text-xs text-warning-700" data-testid="idp-routing-needs-verified">
                Verify or attest a domain first.
              </span>
            )}
          </span>
        </label>
        {wouldMix && (
          <p className="flex items-start gap-1.5 text-xs text-warning-700" data-testid="idp-routing-mixed">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              {others.map((o) => o.displayName).join(', ')} {others.length === 1 ? 'is' : 'are'} enabled and route
              {others.length === 1 ? 's' : ''} {draft === 'domains' ? 'everyone in the tenant' : 'by domain'}. A tenant's enabled
              providers must all route the same way: change {others.length === 1 ? 'it' : 'them'} first, or disable{' '}
              {others.length === 1 ? 'it' : 'them'}.
            </span>
          </p>
        )}
        <div className="flex justify-end">
          <button
            type="button"
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={draft === current || wouldMix || saving}
            onClick={() => void openDialog({ kind: 'routing', to: draft })}
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5 inline" />
            Save routing…
          </button>
        </div>
      </fieldset>

      <RoutingPreviewDialog
        isOpen={!!dialog}
        title={dialogTitle}
        description={dialogDescription}
        preview={preview}
        loading={previewLoading}
        error={previewError}
        busy={saving}
        variant={dialog?.kind === 'remove' ? 'danger' : 'primary'}
        confirmText={dialog?.kind === 'routing' ? 'Save routing' : dialog?.kind === 'remove' ? 'Remove domain' : 'Attest ownership'}
        confirmDisabled={dialog?.kind === 'attest' && reason.trim().length < 3}
        providerName={p.displayName}
        onConfirm={() => void confirm()}
        onCancel={closeDialog}
      >
        {dialog?.kind === 'attest' && (
          <div>
            <label htmlFor="idp-attest-reason" className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-danger-600">*</span>
            </label>
            <input
              id="idp-attest-reason"
              className="input-base w-full"
              placeholder="e.g. lab IdP realm, no public DNS"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              disabled={saving}
            />
            <p className="mt-1 text-xs text-gray-500">At least 3 characters; kept in the activity log.</p>
          </div>
        )}
        {dialog?.kind === 'remove' && (
          <div>
            <label htmlFor="idp-remove-reason" className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
            <input
              id="idp-remove-reason"
              className="input-base w-full"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              disabled={saving}
            />
          </div>
        )}
      </RoutingPreviewDialog>
    </div>
  );
};

export default ProviderRoutingCard;
